/**
 * T058: Prompt Content Viewer Component
 * Handles display of text/chat/multipart prompt content types
 */

import { cn } from '@/lib/utils'
import type { PromptContent } from '@/services/prompts'
import type { ChatMessage } from '@arcanum/core'

interface PromptContentViewerProps {
  content: PromptContent
  className?: string
}

export function PromptContentViewer({ content, className }: PromptContentViewerProps) {
  if (content.type === 'text') {
    return (
      <div className={cn('whitespace-pre-wrap font-mono text-sm', className)}>
        {content.text}
      </div>
    )
  }

  if (content.type === 'chat') {
    return (
      <div className={cn('space-y-4', className)}>
        {content.messages.map((message, index) => (
          <ChatMessageDisplay key={index} message={message} />
        ))}
      </div>
    )
  }

  // Fallback for unknown types
  return (
    <div className={cn('text-muted-foreground', className)}>
      Unknown content type
    </div>
  )
}

interface ChatMessageDisplayProps {
  message: ChatMessage
}

function ChatMessageDisplay({ message }: ChatMessageDisplayProps) {
  const roleColors: Record<string, string> = {
    system: 'bg-purple-100 border-purple-200 dark:bg-purple-950 dark:border-purple-800',
    user: 'bg-blue-100 border-blue-200 dark:bg-blue-950 dark:border-blue-800',
    assistant: 'bg-green-100 border-green-200 dark:bg-green-950 dark:border-green-800',
  }

  const roleLabels: Record<string, string> = {
    system: 'System',
    user: 'User',
    assistant: 'Assistant',
  }

  return (
    <div className={cn('rounded-lg border p-4', roleColors[message.role] || 'bg-muted')}>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {roleLabels[message.role] || message.role}
      </div>
      <div className="whitespace-pre-wrap font-mono text-sm">
        {message.content}
      </div>
    </div>
  )
}
